// src/components/Sidebar.js
import React, { useState } from 'react';
import { apiClientRaw } from '../utils/api';
import { Box, Typography, styled, Drawer, IconButton, Collapse } from '@mui/material';
import { LogoutOutlined, Menu as MenuIcon, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearAuthData } from '../../store/slices/authSlice';
import { toast } from 'react-toastify';
 

const SidebarTypography = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  color: 'inherit',
  transition: 'color 0.3s',
}));

const SidebarBox = styled(Box)(({ theme, active }) => ({
  height: '6vh',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 3),
  cursor: 'pointer',
  gap: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: active ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
  color: active ? '#2563eb' : '#333333',
  transition: 'background-color 0.3s, color 0.3s',
  '&:hover': {
    background: active ? 'rgba(37, 99, 235, 0.15)' : 'rgba(37, 99, 235, 0.05)',
    color: '#2563eb',
  },
  '& svg': {
    fontSize: '1.5rem',
    color: active ? '#2563eb' : '#2563eb',
  },
}));

const SubItemBox = styled(SidebarBox)(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  backgroundColor: 'rgba(248, 249, 250, 0.8)',
  color: '#666666',
  '&:hover': {
    background: 'rgba(37, 99, 235, 0.05)',
    color: '#2563eb',
  },
}));

const Sidebar = ({ menuItems }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  // Get refresh token from Redux state
  const refreshToken = useSelector((state) => state.auth.refreshToken);
  const activeItem = location.pathname;

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (link) => {
    toggleDrawer();
    navigate(link);
  };

  const toggleExpand = (name) => {
    setExpandedItems((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleLogout = async () => {
    try {
      const response = await apiClientRaw('/logout/', 'POST', { refresh: refreshToken });
      
      if (response.status === 200 || response.status === 205) {
        // Clear auth data from the Redux store
        dispatch(clearAuthData());
        localStorage.removeItem('accessToken');
        
        // Show success toast notification
        toast.success('Logged out successfully.');

        // Redirect to the login page
        navigate('/');
      } else {
        toast.error('Logout failed. Please try again.');
      }
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed. Please try again.');
    }
  };

  return (
    <>
      <IconButton onClick={toggleDrawer} sx={{ color: 'black' }}>
        <MenuIcon />
      </IconButton>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer}
        PaperProps={{
          sx: { 
            width: '20vw', 
            background: 'white',
            borderRight: '1px solid rgba(37, 99, 235, 0.2)'
          },
        }}
      >
        <Box display="flex" flexDirection="column" height="100vh">
          <Box bgcolor="rgba(255, 255, 255, 0.05)" display="flex" alignItems="center" justifyContent="center" height="10%" sx={{ borderBottom: '1px solid rgba(37, 99, 235, 0.2)' }}>
            <img src="/Probate.jpeg" alt="Logo" style={{ maxWidth: '80%', height: '80%' }} />
          </Box>
          <Box mt={4}>
            {menuItems.map((item) => (
              <Box key={item.name}>
                <SidebarBox
                  active={activeItem === item.path}
                  onClick={() => item.subItems ? toggleExpand(item.name) : handleNavigation(item.path)}
                >
                  {item.icon}
                  <SidebarTypography variant="h6">{item.name}</SidebarTypography>
                  {item.subItems && (expandedItems[item.name] ? <ExpandLess /> : <ExpandMore />)}
                </SidebarBox>
                
                {item.subItems && (
                  <Collapse in={expandedItems[item.name]} timeout="auto" unmountOnExit>
                    <Box>
                      {item.subItems.map((subItem) => (
                        <SubItemBox
                          key={subItem.name}
                          active={activeItem === subItem.path}
                          onClick={() => handleNavigation(subItem.path)}
                        >
                          <SidebarTypography variant="body2">{subItem.name}</SidebarTypography>
                        </SubItemBox>
                      ))}
                    </Box>
                  </Collapse>
                )}
              </Box>
            ))}
          </Box>

          {/* Static Logout Button */}
          <SidebarBox sx={{ mt: 'auto', mb: 4 }} onClick={handleLogout}>
            <LogoutOutlined fontSize="large" sx={{ color: '#2563eb' }} />
            <SidebarTypography variant="h6">Logout</SidebarTypography>
          </SidebarBox>
        </Box>
      </Drawer>
    </>
  );
};

export default Sidebar;
